/**
 * Works out which body rows a sheet has, and what number each one prints.
 *
 * The three canvases (composer, preview, PDF) each used to hardcode 49 rows numbered 11-108.
 * That only held while every template used those numbers. Real ISA forms do not: a valve runs
 * 11-74 / 75-138 on sheet 1, 211-245 / 246-280 on sheet 2, and 411-474 / 475-538 on Application
 * Specific. Against a fixed 11-108 grid, sheet 1 lost everything above 108, and sheets 2 and 3
 * matched nothing at all - they rendered as an empty grid still labelled 11-108.
 *
 * So the rows come from the template type's spec in UtlCfg_DSTemplate - the form's own printed
 * ranges. A type with no spec falls back to the boxes actually present. Either way the column
 * comes from DS_Zone, falling back to the old band for rows written before the zone was stored.
 */

const BLANK_ROWS = 10;          // spare rows at the foot of each column, so boxes can still be added
const DEFAULT_L_FROM = 11;      // the classic layout, used only when a sheet has no body rows yet
const DEFAULT_R_FROM = 60;
const DEFAULT_COUNT = 49;

// The forms number each sheet from a frozen base: content sheet 1 from 11, content sheet 2 from
// 211, and Application Specific from 411 - which it does whether it lands on sheet 2 or sheet 3,
// so the base follows the sheet's ROLE, not its position. Application Specific is always the last
// sheet, the same rule the sheet tabs label by. A sheet with no body rows to infer from is
// numbered this way so a page authored from scratch lines up with the reference documents
// instead of repeating sheet 1's numbers.
const CONTENT_BASE = 200;
const APPSPEC_BASE = 400;

function fallbackBase(sheet, sheetCount) {
  if (sheet <= 1) return 0;
  if (sheetCount && sheet >= sheetCount) return APPSPEC_BASE;
  return (sheet - 1) * CONTENT_BASE;
}

// Which sheet this list belongs to. The caller has already filtered to one sheet, so ANY box
// answers it - including a header or centre box. Looking only at body boxes was the bug: a
// sheet carrying header content but no body rows looked empty and got sheet 1's numbering.
function sheetOf(boxes) {
  for (const b of boxes) {
    if (b.sheet != null) return b.sheet;
  }
  return 1;
}

function columnOf(box) {
  if (box.dsZone === 'bodyL') return 'L';
  if (box.dsZone === 'bodyR') return 'R';
  // Pre-zone rows: the old bands are what their numbers meant.
  return (box.lineNo ?? 0) <= 59 ? 'L' : 'R';
}

function linesFor(boxes, col) {
  const seen = new Set();
  for (const b of boxes) {
    if (b.zone !== 'body') continue;
    if (columnOf(b) !== col) continue;
    if (b.lineNo != null) seen.add(b.lineNo);
  }
  return [...seen].sort((a, b) => a - b);
}

/**
 * Returns [{ leftLine, rightLine }] - one entry per printed row. Either side can be null when
 * that column runs out.
 *
 * layout is the sheet's UtlCfg_DSTemplate spec (see dsTemplateLayout.js). When it defines the
 * body columns, each column prints its full Line_From..Line_To range and nothing is inferred.
 * A box sitting outside its column's range is still shown, after the range, so an off-spec
 * box stays visible and fixable instead of silently vanishing.
 *
 * Without a layout, sheetCount is only consulted for a sheet with no body rows yet, to tell the
 * last sheet (Application Specific, 411+) from a middle content sheet (211+).
 */
export default function bodyRowPairs(boxes = [], sheetCount = 0, layout = null) {
  let left = linesFor(boxes, 'L');
  let right = linesFor(boxes, 'R');

  const specL = layout?.bodyL;
  const specR = layout?.bodyR;
  if (specL?.lineFrom != null || specR?.lineFrom != null) {
    const fromSpec = (spec, present) => {
      if (spec?.lineFrom == null) return present;
      const lines = [];
      for (let n = spec.lineFrom; n <= spec.lineTo; n += 1) lines.push(n);
      return lines.concat(present.filter(n => n < spec.lineFrom || n > spec.lineTo));
    };
    left = fromSpec(specL, left);
    right = fromSpec(specR, right);
  } else if (left.length === 0 && right.length === 0) {
    // No body rows to infer from, so lay out a blank grid on this sheet's own numbering base.
    const base = fallbackBase(sheetOf(boxes), sheetCount);
    left = Array.from({ length: DEFAULT_COUNT }, (_, i) => base + DEFAULT_L_FROM + i);
    right = Array.from({ length: DEFAULT_COUNT }, (_, i) => base + DEFAULT_R_FROM + i);
  } else {
    // Spare rows continue each column's own sequence, so there is somewhere to drop a new box
    // without inventing a number from another sheet's range.
    const extend = (arr, fallbackFrom) => {
      const next = arr.length ? arr[arr.length - 1] + 1 : fallbackFrom;
      return arr.concat(Array.from({ length: BLANK_ROWS }, (_, i) => next + i));
    };
    left = extend(left, DEFAULT_L_FROM);
    right = extend(right, DEFAULT_R_FROM);
  }

  const rows = Math.max(left.length, right.length);
  return Array.from({ length: rows }, (_, i) => ({
    leftLine: left[i] ?? null,
    rightLine: right[i] ?? null,
  }));
}
